import stripAnsi from 'strip-ansi';
import type { ProjectError, ProjectErrorLocation } from './plugins/events.js';

const hasStringStderr = (
  maybeError: unknown
): maybeError is { stderr: string } => {
  return (
    typeof maybeError === 'object' &&
    maybeError != null &&
    'stderr' in maybeError &&
    typeof maybeError.stderr === 'string'
  );
};

type Section =
  | 'message'
  | 'hint'
  | 'errorReference'
  | 'location'
  | 'stacktrace';

// Section markers rendered by astro's formatErrorMessage (core/messages.ts)
const SECTION_MARKERS: Record<string, Section> = {
  'Hint:': 'hint',
  'Error reference:': 'errorReference',
  'Location:': 'location',
  'Stack trace:': 'stacktrace',
};

const TIMESTAMPED_LOG_LINE = /^\d{2}:\d{2}:\d{2}\s/;
// The build runs through the package manager, which writes its own warnings
// to stderr: `npm WARN ...` / `npm notice ...`, yarn classic `warning ...`,
// yarn berry `➤ YN0002: ...`.
// `warning` requires a trailing space so that real diagnostics such as
// React's `Warning: ...` lines are not treated as noise.
const PACKAGE_MANAGER_NOISE_LINE = /^(npm (warn|notice)\b|warning |➤ YN\d+)/i;
const FILE_LINE_COLUMN = /^(.+):(\d+):(\d+)$/;

const joinLines = (lines: string[]): string | undefined => {
  return lines.join('\n') || undefined;
};

const isNoise = (line: string) => PACKAGE_MANAGER_NOISE_LINE.test(line);

const dropEdgeNoise = (lines: string[]): string[] => {
  const first = lines.findIndex((line) => !isNoise(line));
  const last = lines.findLastIndex((line) => !isNoise(line));
  return first === -1 ? lines : lines.slice(first, last + 1);
};

const parseLocationLine = (line: string): ProjectErrorLocation => {
  const match = FILE_LINE_COLUMN.exec(line);
  const [, file, lineNumber, column] = match ?? [];

  if (file == null || lineNumber == null || column == null) {
    return { file: line };
  }

  return {
    file,
    line: Number(lineNumber),
    column: Number(column),
  };
};

const parseSections = (stderr: string): Omit<ProjectError, 'raw'> => {
  const sections: Record<Section, string[]> = {
    message: [],
    hint: [],
    errorReference: [],
    location: [],
    stacktrace: [],
  };
  let section: Section = 'message';

  for (const line of stderr.split('\n')) {
    const trimmed = line.trim();

    if (!trimmed || TIMESTAMPED_LOG_LINE.test(trimmed)) {
      continue;
    }

    const nextSection = SECTION_MARKERS[trimmed];
    if (nextSection != null) {
      section = nextSection;
      continue;
    }

    sections[section].push(trimmed);
  }

  const [locationLine] = sections.location;

  return {
    message: joinLines(dropEdgeNoise(sections.message)),
    location:
      locationLine != null ? parseLocationLine(locationLine) : undefined,
    stack: joinLines(dropEdgeNoise(sections.stacktrace)),
  };
};

// Parsing error is defensive by design, it should not break, since we're using it in error handling.
export const parseStdErr = (e: unknown): ProjectError | undefined => {
  let stderr: string | undefined;
  try {
    stderr = hasStringStderr(e) ? stripAnsi(e.stderr) : undefined;
  } catch {
    return undefined;
  }

  if (stderr == null || stderr === '') {
    return undefined;
  }

  try {
    return { raw: stderr, ...parseSections(stderr) };
  } catch {
    return { raw: stderr };
  }
};
